/**
 * TokenManager — single source of truth for Cedar JWT lifecycle.
 *
 * Why this exists
 * ===============
 * Before Wave 1.5, every native service that issued an authenticated fetch
 * called `getAuthToken()` to read the JWT from secure storage, then made
 * the request. If the JWT had rotated mid-operation (a 30-second HealthKit
 * enrichment loop, a 9-minute LLM plan synthesis), the request 401-ed with
 * no recovery path. The actual refresh logic lived in a closure inside
 * UnifiedAuth0Provider.tsx (`tryRefreshTokenWithClient`) — reachable only
 * from inside the React provider, not from any service.
 *
 * TokenManager extracts that refresh logic into a framework-agnostic class
 * so any caller — service, hook, fetch wrapper — can request a valid token
 * or force a refresh. The provider keeps owning the SDK lifecycle (creating
 * the Auth0 client, handling boot/login/logout flows); TokenManager owns
 * the token rotation.
 *
 * Design properties
 * -----------------
 * 1. Concurrency dedup. If five callers hit `refresh()` simultaneously
 *    (e.g. five poll intervals all 401 at once), exactly one Auth0 SDK
 *    call fires; all five callers await the same promise. The provider's
 *    pre-existing `_refreshInProgress` boolean had this property only
 *    inside the React tree — we need it everywhere.
 * 2. Proactive expiry. `getValidToken()` decodes the JWT's exp claim and
 *    refreshes if within `proactiveRefreshSec` of expiry — the JWT never
 *    *should* go stale mid-call. Reactive 401-handling in fetchWithAuth
 *    is the safety net.
 * 3. Failure surfaces as null, not exceptions. Refresh failures (network,
 *    invalid_grant, backend reject) all return null. Callers decide
 *    whether to retry, log out the user, or propagate the 401. No silent
 *    swallowing — the provider's logout flow watches for repeated nulls.
 * 4. Reactive state via subscribers. The React provider subscribes once
 *    on mount and re-syncs its state whenever a refresh succeeds. No
 *    polling, no race with the storage layer.
 *
 * What this is NOT
 * ----------------
 * - Not an HTTP client. fetchWithAuth (Phase A day 2) wraps `fetch` and
 *   uses TokenManager internally. Services should use fetchWithAuth, not
 *   TokenManager directly, except in the provider itself.
 * - Not a session manager. Login, logout, and Auth0 SDK lifecycle stay
 *   in UnifiedAuth0Provider — TokenManager only knows about the JWT.
 * - Not a multi-tenant abstraction. There's one logged-in user per app
 *   instance; the singleton is correct here. _resetTokenManagerForTests
 *   exists for unit-test isolation only.
 *
 * See web/src/auth/__tests__/tokenManager.test.ts for the contract.
 */

import { Platform } from 'react-native';
import { secureTokenStorage } from '../services/secureTokenStorage';
import { API_BASE_URL } from '../utils/api';
import { auth0Config } from './auth0-config';

// The Auth0 SDK is platform-specific (`react-native-auth0` on native,
// `@auth0/auth0-react` on web). Both expose an `auth.refreshToken` /
// `auth.userInfo` shape; we type as `any` here to avoid importing both
// SDKs from the same module. The provider passes its initialized client
// to setAuth0Client on boot.
type Auth0Client = {
  auth: {
    refreshToken: (args: { refreshToken: string; scope?: string }) => Promise<{
      accessToken: string;
      refreshToken?: string;
      idToken?: string;
    }>;
    userInfo: (args: { token: string }) => Promise<unknown>;
  };
};

type RefreshListener = () => void;

export interface JwtPayload {
  exp?: number | string;
  [k: string]: unknown;
}

/**
 * Decode a JWT's payload claims without verifying the signature.
 *
 * Lifted from contexts/AuthContext.tsx:88-110 and
 * auth/UnifiedAuth0Provider.tsx:347-362 — both had the same inline
 * pattern. Centralizing here so all expiry checks read the same exp
 * claim with the same edge-case handling.
 *
 * Returns null on any parse failure. Callers MUST treat unparseable
 * tokens as "unknown expiry" rather than "expired" — clearing on parse
 * failure was rejected as too aggressive (could be a JWT format we don't
 * yet handle, e.g. Auth0 rotating to a new claim layout).
 *
 * Signature verification is the backend's job (FastAPI auth middleware
 * in routers/auth0_auth.py). The client only needs the exp claim to
 * decide whether to refresh proactively.
 */
export function decodeJwtPayload(jwt: string): JwtPayload | null {
  try {
    const parts = jwt.split('.');
    if (parts.length !== 3) return null;
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    // base64url drops trailing '=' padding; restore so atob/Buffer can decode.
    const padNeeded = (4 - (base64.length % 4)) % 4;
    base64 += '='.repeat(padNeeded);
    const decoded = Platform.OS === 'web'
      ? atob(base64)
      : Buffer.from(base64, 'base64').toString('utf-8');
    const parsed = JSON.parse(decoded);
    if (parsed === null || typeof parsed !== 'object') return null;
    return parsed as JwtPayload;
  } catch {
    return null;
  }
}

/**
 * Returns true if the JWT will expire within `withinSec` seconds.
 *
 * Returns false when:
 *   - The JWT is unparseable (treat as unknown — let the next API call
 *     surface a 401, which fetchWithAuth handles reactively).
 *   - The exp claim is missing (some legacy or test tokens).
 *   - The exp claim is non-finite (corrupt token).
 *
 * The asymmetry is deliberate: an over-aggressive "true" answer forces
 * unnecessary refreshes (Auth0 quota waste, latency); an over-aggressive
 * "false" answer is caught reactively when the request 401s.
 */
export function isJwtExpiringSoon(jwt: string, withinSec: number): boolean {
  const payload = decodeJwtPayload(jwt);
  if (!payload) return false;
  const expRaw = payload.exp;
  const exp = typeof expRaw === 'string' ? parseInt(expRaw, 10) : expRaw;
  if (typeof exp !== 'number' || !Number.isFinite(exp)) return false;
  // exp is seconds since epoch; Date.now() is ms.
  return exp * 1000 < Date.now() + withinSec * 1000;
}

/**
 * Why a refresh failed. The distinction is load-bearing: it decides whether a
 * caller may destroy the session.
 *
 * Before 2026-08-14 every failure collapsed to `null`, so the only honest thing
 * a caller could do with a failure was guess — and the boot actors guessed
 * "terminal", which is how a user who was merely OFFLINE, or who hit a boot
 * ordering race, got signed out and had their Auth0 refresh token deleted.
 *
 * The asymmetry is deliberate and must stay: only `rejected` (Auth0 positively
 * refused the credential) and `no_refresh_token` (there is nothing to refresh
 * with) may end a session. EVERYTHING ELSE — including anything we failed to
 * recognise — is transient, and the tokens must survive it.
 */
export type RefreshFailure =
  | 'none'              // refresh succeeded
  | 'no_client'         // SDK not wired yet (boot ordering) — says nothing about the credential
  | 'no_refresh_token'  // nothing to refresh with — terminal
  | 'rejected'          // Auth0 refused the refresh token (invalid_grant) — terminal
  | 'network'           // offline / DNS / timeout / unrecognised — transient
  | 'backend';          // our own /api/auth0/verify failed — transient

export interface RefreshResult {
  token: string | null;
  failure: RefreshFailure;
}

/**
 * Auth0 error identifiers that positively mean "this refresh token is dead".
 * Matched against structured fields only (code/name/error/status), because a
 * substring hunt through free-text messages is how a transient failure gets
 * mistaken for a revoked credential.
 */
const TERMINAL_AUTH0_ERROR_CODES = new Set([
  'invalid_grant',
  'invalid_refresh_token',
  'access_denied',
  'login_required',
  'consent_required',
  'unauthorized',
]);

/**
 * Classify an Auth0 SDK refresh error. Fails SAFE: anything we cannot
 * positively identify as a credential rejection is reported as transient, so
 * an unrecognised SDK error can never destroy a session.
 *
 * Structured fields ONLY — never `err.message`. react-native-auth0's AuthError
 * populates `.name` from the OAuth `error` field, `.json` from
 * `{error, error_description}` and `.status` from the HTTP status, so the
 * structured path already covers every real rejection. `.message`, by
 * contrast, comes from `error_description`: localized, free prose, and subject
 * to change by Auth0. Matching it could only ever move a verdict TOWARD
 * 'rejected' — the direction that costs a user their session.
 */
export function classifyAuth0RefreshError(err: unknown): 'rejected' | 'network' {
  const e = err as Record<string, any> | null | undefined;
  if (!e || typeof e !== 'object') return 'network';

  const codeFields = [e.code, e.name, e.error, e.errorCode, e.type, e.json?.error];
  for (const field of codeFields) {
    if (typeof field === 'string' && TERMINAL_AUTH0_ERROR_CODES.has(field.trim().toLowerCase())) {
      return 'rejected';
    }
  }

  const status = typeof e.status === 'number' ? e.status
    : typeof e.statusCode === 'number' ? e.statusCode
    : null;
  if (status === 401 || status === 403) return 'rejected';

  // NOTE: a bare HTTP 400 is deliberately NOT terminal. Auth0 returns 400 for
  // invalid_grant, but always WITH the code — which the field check above has
  // already caught. A 400 carrying no recognisable code is more likely a
  // malformed request than a dead credential, so it retries instead.
  return 'network';
}

/**
 * How long to wait before another Auth0 round-trip after a TRANSIENT failure.
 * Small on purpose: this exists to stop four pollers plus the boot path from
 * machine-gunning Auth0 while a device is offline, not to delay recovery — a
 * user who regains connectivity should heal within seconds, not minutes.
 * Terminal failures get no backoff: the caller acts on them immediately.
 */
const TRANSIENT_RETRY_BACKOFF_MS = 5_000;
const TRANSIENT_RETRY_JITTER_MS = 5_000;
/** The provider wires the SDK within a tick or two of boot — barely wait. */
const NO_CLIENT_RETRY_BACKOFF_MS = 1_000;

/**
 * How long the SHARED refresh promise may stay unsettled before every caller
 * waiting on it is released with a transient failure.
 *
 * Not a cancellation of the refresh — the underlying work keeps running and
 * still persists its rotation (see refreshDetailed). This only bounds how
 * long ONE stuck attempt may hold the whole app hostage.
 *
 * 45s is deliberately longer than any single leg. A real refresh is three
 * network round-trips (Auth0 refreshToken, Auth0 userInfo, our /auth0/verify)
 * and can legitimately be slow on a bad connection; cutting it short would
 * turn recoverable slowness into an avoidable re-login prompt. The job here
 * is only to make "never" impossible.
 */
const REFRESH_DEADLINE_MS = 45_000;

/**
 * Resolve with `work`, or with a transient failure once `ms` elapses —
 * whichever comes first. `work` is never cancelled.
 *
 * ⚠️ `setTimeout` is not a wall clock in React Native: a backgrounded app
 * suspends JS timers, so a deadline armed before backgrounding fires on the
 * next foreground rather than on time. That is acceptable and is in fact the
 * moment it matters — the user is looking at the app again, and the slot is
 * released before their first interaction rather than never.
 */
function withRefreshDeadline(
  work: Promise<RefreshResult>,
  ms: number,
): Promise<RefreshResult> {
  return new Promise<RefreshResult>((resolve) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      console.log('AUTH_REFRESH_DEADLINE_RELEASED');
      resolve({ token: null, failure: 'network' });
    }, ms);

    void work.then((result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(result);
    });
  });
}

/**
 * Read a JWT's `iat` claim in seconds, or 0 when it is absent/unparseable.
 * Shared by the refresh bookkeeping and the stale-credential detector so both
 * sides of that comparison extract the value identically.
 */
function _extractIatSeconds(jwt: string): number {
  const payload = decodeJwtPayload(jwt);
  if (!payload) return 0;
  const raw = (payload as Record<string, unknown>).iat;
  const n = typeof raw === 'string' ? parseInt(raw, 10) : raw;
  return typeof n === 'number' && Number.isFinite(n) ? n : 0;
}

class TokenManager {
  private auth0Client: Auth0Client | null = null;
  private refreshInFlight: Promise<RefreshResult> | null = null;
  /** Bumped per attempt so a late-landing stale refresh can identify itself. */
  private refreshGeneration = 0;
  private listeners = new Set<RefreshListener>();
  private lastRefreshSuccessAtMs = 0;
  private lastRefreshIatSec = 0;
  private retryNotBeforeMs = 0;
  private lastFailure: RefreshFailure = 'none';

  /**
   * Wire the Auth0 SDK client. Called by UnifiedAuth0Provider on boot
   * once the SDK is initialized. Until this is called, refresh() returns
   * null (the boot-race case — surfaces as a 401 to the user, the
   * provider's existing boot flow recovers).
   */
  setAuth0Client(client: Auth0Client): void {
    this.auth0Client = client;
  }

  /**
   * Subscribe to refresh-success events. The listener fires once per
   * successful refresh — useful for the React provider to re-read
   * storage and re-render. Returns an unsubscribe function.
   *
   * Listener throws are isolated: one bad listener cannot block others
   * from being notified. This matters because the provider's listener
   * runs setUser/setIsAuthenticated, and a future subscriber (e.g. a
   * metrics emitter) shouldn't be able to break auth state.
   */
  subscribe(listener: RefreshListener): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  /**
   * Get a valid Cedar JWT, proactively refreshing if it's within
   * `proactiveRefreshSec` seconds of expiry. Returns null when no token
   * is stored or when refresh fails.
   *
   * fetchWithAuth() uses this internally before every request so the
   * outgoing JWT is the freshest possible. Direct callers (rare —
   * mostly the provider itself) can call it for read-only purposes.
   */
  async getValidToken(proactiveRefreshSec = 60): Promise<string | null> {
    const token = await secureTokenStorage.getCedarJwt();
    if (!token) return null;
    if (isJwtExpiringSoon(token, proactiveRefreshSec)) {
      const refreshed = await this.refresh();
      if (refreshed) return refreshed;

      // The refresh failed, but PROACTIVE means the token we hold is not
      // actually expired yet — it is merely inside the warning window. Serving
      // null here would return a synthetic 401 to every caller for the whole
      // backoff, which downstream reads as "your session is dead": empty
      // cards, "Authentication required" throws, a false sign-in prompt.
      //
      // This matters most immediately after a refresh deadline fires, which is
      // exactly when the network is bad and the app should be degrading
      // gracefully rather than looking logged out. Use the seconds we still
      // have; a genuinely expired token falls through to null below.
      if (!isJwtExpiringSoon(token, 0)) {
        console.log('AUTH_SERVING_UNEXPIRED_TOKEN_AFTER_FAILED_REFRESH');
        return token;
      }
      return null;
    }
    return token;
  }

  /**
   * Force a refresh. Returns the new Cedar JWT or null if refresh fails.
   *
   * Concurrency: if a refresh is already in flight, all callers await
   * the same Promise and the Auth0 SDK is hit exactly once. Once the
   * in-flight promise settles (success or failure), subsequent calls
   * fire a fresh refresh — the dedup is per-attempt, not permanent.
   */
  async refresh(): Promise<string | null> {
    return (await this.refreshDetailed()).token;
  }

  /**
   * Same refresh, same single-flight promise — but the caller also learns WHY
   * it failed, which is the only way to decide whether ending the session is
   * justified. Callers that can destroy state (the boot actors) MUST use this;
   * callers that only need a token (fetchWithAuth) can keep using refresh().
   *
   * Both entry points share one in-flight promise, so a poller storm and the
   * boot path colliding still produce exactly one Auth0 SDK call.
   */
  async refreshDetailed(): Promise<RefreshResult> {
    if (this.refreshInFlight) return this.refreshInFlight;

    // Inside a transient-failure backoff window, answer from the last result
    // instead of hitting Auth0 again. The replayed answer is always a
    // NON-terminal failure (terminal ones set no backoff), so a caller acting
    // on it can only ever choose to keep the session — never to end it.
    if (Date.now() < this.retryNotBeforeMs) {
      return { token: null, failure: this.lastFailure };
    }

    // Generation stamp. A refresh that outlives its deadline keeps running
    // (see below); when it finally lands, this is how it knows whether a
    // newer attempt has since superseded it.
    const generation = ++this.refreshGeneration;

    const work = this._doRefresh(generation)
      // _doRefresh catches its own failures, but a storage read throwing
      // before the first try/catch would reject this promise — and a rejected
      // refresh propagates out of fetchWithAuth as an exception instead of a
      // Response. Total by construction: this can only ever resolve.
      .catch(() => ({ token: null, failure: 'network' as RefreshFailure }));

    // ── THE LOCKOUT FIX (2026-08-31, mattmayer — 8h43m) ──────────────────
    //
    // What produced 8h43m of dead app was not one hung request. It was THIS
    // ONE SHARED PROMISE never settling: `refreshInFlight` is handed to every
    // concurrent caller, and the `.finally` that clears it only runs when it
    // settles. One half-open socket inside _doRefresh and every later caller
    // awaits the same corpse — permanently, because the backoff armed by
    // _recordAttempt is also downstream of that settle. getValidToken() sits
    // in front of every fetchWithAuth call, so within ~60s (as the JWT ages
    // into the proactive window) the whole app is gone.
    //
    // So bound THE SHARED PROMISE, not the work. On deadline this resolves
    // transiently, the slot clears, and the next caller is free.
    //
    // We deliberately do NOT abort the underlying _doRefresh, and it needs no
    // salvage continuation:
    //
    //   - The dangerous half of a refresh is Auth0 consuming the old refresh
    //     token and minting a rotated one we never record. That is ALREADY
    //     handled: _doRefresh persists the rotation eagerly the moment it
    //     exists (see AUTH_ROTATED_TOKEN_PERSISTED), before userInfo and
    //     before the verify POST, precisely so a later failure cannot strand
    //     the user holding a spent token. A late completion therefore
    //     salvages itself through code that already exists and is tested.
    //   - Racing an abort against the SDK call would only widen the window in
    //     which a rotation is in flight and unrecorded.
    //
    // A timeout resolves as 'network' — transient. It must never be
    // classifiable as terminal: a wedged socket says nothing about whether
    // the credential is valid, and ending a session over one would be a worse
    // outcome than the hang it replaces.
    this.refreshInFlight = withRefreshDeadline(work, REFRESH_DEADLINE_MS)
      .then((result) => {
        // Only the current attempt owns the bookkeeping. Without this, a
        // late-landing stale attempt could overwrite a newer verdict and
        // clear the backoff window that newer attempt just armed.
        if (generation === this.refreshGeneration) {
          this._recordAttempt(result.failure);
        }
        return result;
      })
      .finally(() => {
        // Only clear the slot if it is still ours — a superseding attempt has
        // already installed its own promise there.
        if (generation === this.refreshGeneration) {
          this.refreshInFlight = null;
        }
      });
    return this.refreshInFlight;
  }

  /** Arm the backoff window for transient failures only. */
  private _recordAttempt(failure: RefreshFailure): void {
    this.lastFailure = failure;
    if (failure === 'none' || failure === 'rejected' || failure === 'no_refresh_token') {
      this.retryNotBeforeMs = 0;
      return;
    }
    const base = failure === 'no_client'
      ? NO_CLIENT_RETRY_BACKOFF_MS
      : TRANSIENT_RETRY_BACKOFF_MS + Math.random() * TRANSIENT_RETRY_JITTER_MS;
    this.retryNotBeforeMs = Date.now() + base;
  }

  /**
   * The actual refresh sequence. Lifted from
   * UnifiedAuth0Provider.tryRefreshTokenWithClient (lines 198-275) so
   * the logic is reachable from any service via TokenManager.
   *
   * Steps:
   *   1. Read refresh token from secure storage (null → bail).
   *   2. Call Auth0 SDK refreshToken with the stored refresh token
   *      and the configured scope.
   *   3. Call Auth0 SDK userInfo to get the latest profile claims
   *      (required by /api/auth0/verify backend endpoint).
   *   4. POST credentials.accessToken + userInfo to the backend
   *      verify endpoint to mint a new Cedar JWT.
   *   5. Save accessToken / refreshToken / cedarJwt back to secure
   *      storage. If Auth0 didn't rotate the refresh token, preserve
   *      the existing one.
   *   6. Save user data if returned.
   *   7. Notify subscribers so React state re-syncs.
   *
   * Any step failing returns null — the caller (fetchWithAuth or the
   * provider) decides what to do (retry once, log out, surface 401).
   */
  private async _doRefresh(generation?: number): Promise<RefreshResult> {
    if (!this.auth0Client) {
      // Provider hasn't wired the SDK client yet (boot ordering). This says
      // NOTHING about the credential, so it must never end a session — the
      // provider's own boot flow owns recovery from here.
      return { token: null, failure: 'no_client' };
    }

    const refreshToken = await secureTokenStorage.getRefreshToken();
    if (!refreshToken) return { token: null, failure: 'no_refresh_token' };

    let credentials: {
      accessToken: string;
      refreshToken?: string;
      idToken?: string;
    };
    try {
      credentials = await this.auth0Client.auth.refreshToken({
        refreshToken,
        scope: auth0Config.scope,
      });
    } catch (err) {
      // The ONLY place a session may legitimately be declared over — and only
      // when Auth0 positively refused the credential (invalid_grant class).
      // Anything unrecognised classifies as transient, so an offline user or a
      // flaky SDK call keeps their tokens instead of being signed out.
      return { token: null, failure: classifyAuth0RefreshError(err) };
    }

    // ROTATION HAZARD (2026-08-14): the call above CONSUMED `refreshToken`.
    // Under Auth0 refresh-token rotation the one in `credentials` is the ONLY
    // live credential from this instant on — and five branches between here
    // and the _persist() below return early (userInfo throws, the verify fetch
    // throws, !response.ok, json() throws, missing/non-string data.token).
    // Every one of those used to discard the rotated token and leave the user
    // holding a spent one: permanently dead, and the next refresh reads as a
    // hard `rejected` — a forced sign-out caused entirely by our own bookkeeping.
    //
    // So persist the rotation the moment it exists, before anything else can
    // fail. Two args deliberately: saveTokens() only writes cedar_jwt when a
    // third arg is passed (`if (cedarJwt)`), so this preserves the existing
    // Cedar JWT rather than clobbering it — the full write below still lands
    // the new one on the success path.
    if (credentials.refreshToken && credentials.refreshToken !== refreshToken) {
      try {
        await secureTokenStorage.saveTokens(credentials.accessToken, credentials.refreshToken);
        console.log('AUTH_ROTATED_TOKEN_PERSISTED');
      } catch {
        // Best-effort, same posture as the _persist catch below: a storage
        // failure must not turn a successful Auth0 rotation into a refresh
        // failure the boot actors might read as terminal.
      }
    }

    let userInfo: unknown;
    try {
      userInfo = await this.auth0Client.auth.userInfo({
        token: credentials.accessToken,
      });
    } catch {
      // The refresh itself already succeeded, so the credential is fine —
      // whatever went wrong here is transient by construction.
      return { token: null, failure: 'network' };
    }

    let response: Response;
    try {
      response = await fetch(`${API_BASE_URL}/api/auth0/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_token: credentials.accessToken,
          auth0_user: userInfo,
        }),
      });
    } catch {
      // Network failure between client and our backend. The Auth0 side
      // succeeded (Auth0 considers the refresh token used; our records
      // are out of sync). Acceptable: next refresh will retry against
      // Auth0 with the new (rotated) refresh token if Auth0 rotated it,
      // or the existing one otherwise. Worst case: user re-logs in.
      return { token: null, failure: 'network' };
    }

    if (!response.ok) return { token: null, failure: 'backend' };

    let data: { token?: string; user?: unknown };
    try {
      data = await response.json();
    } catch {
      return { token: null, failure: 'backend' };
    }

    const newJwt = data.token;
    if (typeof newJwt !== 'string' || !newJwt) return { token: null, failure: 'backend' };

    // Persisting is best-effort: the JWT in hand is genuinely valid, so a
    // SecureStore write failure must not turn a SUCCESSFUL refresh into a
    // failure the boot actors might read as terminal. Before this guard a
    // throw here rejected refresh() itself, which propagated out of
    // fetchWithAuth as an exception instead of a Response — the one shape
    // offlineCompletionQueue's status classifier cannot reason about.
    try {
      // A refresh that outlived its deadline can land after a NEWER refresh
      // has already succeeded and written fresher credentials. Writing ours
      // now would roll the user back onto a spent access token and JWT.
      //
      // The eager rotation-persist above is deliberately NOT guarded: a
      // rotated refresh token is strictly better than the spent one it
      // replaces regardless of ordering, and losing it is the failure that
      // forces a re-login.
      if (generation !== undefined && generation !== this.refreshGeneration) {
        console.log('AUTH_REFRESH_STALE_PERSIST_SKIPPED');
        return { token: null, failure: 'network' };
      }

      await this._persist(
        credentials.accessToken,
        // Preserve existing refresh token if Auth0 didn't rotate it. Some
        // Auth0 configs don't rotate; without this guard we'd lose it.
        //
        // Use `||` (not `??`) to also fall through on the empty-string case
        // — a malformed SDK response could return refreshToken: '', and
        // saving the empty string would brick subsequent refreshes. This
        // matches the original tryRefreshTokenWithClient pattern at
        // UnifiedAuth0Provider.tsx:255. Pinned by
        // 'preserves the existing refresh token if Auth0 returns it as
        // empty string' in the test suite.
        credentials.refreshToken || refreshToken,
        newJwt,
        data.user,
      );
    } catch (e) {
      // Storage is stale; the token is still good for THIS caller — but every
      // other reader (and the next cold start) will keep serving whatever is
      // on disk. saveTokens() writes the canonical key first and the legacy
      // `jwt_token` mirror second, so a failure here can leave the two
      // divergent, which reads to a legacy caller as a whole-session 401 storm
      // while refresh() reports 'none'. Until 2026-08-14 that window was
      // completely silent; make it greppable at least.
      console.warn('TOKEN_PERSIST_PARTIAL', e);
    }

    this.lastRefreshSuccessAtMs = Date.now();
    // Also record the minted token's OWN `iat`, in the server's clock.
    //
    // The wall clock above cannot be compared against a token's iat: the server
    // floors iat to whole seconds, and this line runs AFTER the round-trip, the
    // JSON parse and the awaited persist. So `Date.now()` here is always later
    // than the iat of the very token just minted — comparing them flags that
    // token as stale on its next use, on every healthy device. Comparing
    // server-iat to server-iat makes latency, truncation and device clock skew
    // all cancel.
    this.lastRefreshIatSec = _extractIatSeconds(newJwt);

    // Notify subscribers (React provider) so auth state re-syncs.
    this.listeners.forEach((cb) => {
      try { cb(); } catch { /* one bad listener must not block others */ }
    });

    return { token: newJwt, failure: 'none' };
  }

  /** Timestamp of the last successful refresh (0 = never). Wall-clock, for
   *  callers that want elapsed time. NOT comparable to a token's `iat` — see
   *  lastRefreshIat(). */
  lastRefreshSuccessAt(): number {
    return this.lastRefreshSuccessAtMs;
  }

  /** `iat` (seconds, SERVER clock) of the most recently minted token, or 0 if
   *  this session has never refreshed. This is the only value safe to compare
   *  against another token's `iat`: both come from the same clock at the same
   *  resolution, so a token compares equal to itself. */
  lastRefreshIat(): number {
    return this.lastRefreshIatSec;
  }

  private async _persist(
    accessToken: string,
    refreshToken: string,
    newJwt: string,
    user: unknown,
  ): Promise<void> {
    await secureTokenStorage.saveTokens(accessToken, refreshToken, newJwt);
    if (user) {
      await secureTokenStorage.saveUserData(user);
    }
  }
}

let _instance: TokenManager | null = null;

/**
 * Get the singleton TokenManager. Lazy — instantiates on first call so
 * test environments without the full provider tree can still import the
 * module without side effects.
 */
export function getTokenManager(): TokenManager {
  if (!_instance) _instance = new TokenManager();
  return _instance;
}

/**
 * Test-only: reset the singleton so each test starts with a fresh
 * instance (no leaked subscribers, no leaked refreshInFlight).
 *
 * NEVER call this from production code.
 */
export function _resetTokenManagerForTests(): void {
  _instance = null;
}
